import React, { useState } from "react";
import {
  Table,
  Tag,
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Alert,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Skeleton,
  Empty,
} from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  UserOutlined,
  ShopOutlined,
  PlusOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Purchase, PurchaseStatus, PurchasePaymentStatus } from "../../../Types";
import {
  usePurchasePaymentsQuery,
  useCreatePurchasePaymentMutation,
  useDeletePurchasePaymentMutation,
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
} from "../../../Services";
import { PaymentOption, PaymentOptionValue } from "../../../components/PaymentsOption";
import { PaymentMethodDetails } from "../../../components/PaymentMethodDetails/PaymentMethodDetails";

const { Title, Text } = Typography;
const { Option } = Select;

interface PurchaseViewProps {
  purchase: Purchase;
  onPaymentChange?: () => void;
}

export const PurchaseView: React.FC<PurchaseViewProps> = ({
  purchase,
  onPaymentChange,
}) => {
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [paymentMethodCode, setPaymentMethodCode] = useState<string>("");
  const [paymentDetails, setPaymentDetails] = useState<PaymentOptionValue>({
    amount: purchase.due_amount,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });
  const [form] = Form.useForm();

  const { data: payments = [], isLoading: paymentsLoading } =
    usePurchasePaymentsQuery({
      page: 1,
      limit: 100,
      purchase_id: String(purchase.id),
    });

  const { data: paymentMethods = [] } = useActivePaymentMethodsQuery();
  const { data: bankAccounts = [] } = useActiveBankAccountsQuery();
  const createPaymentMutation = useCreatePurchasePaymentMutation();
  const deletePaymentMutation = useDeletePurchasePaymentMutation();

  const isCompleted = purchase.status === PurchaseStatus.COMPLETED;

  const handleRecordPayment = async (values: any) => {
    try {
      const amount = paymentDetails.amount || 0;
      if (amount <= 0 || amount > purchase.due_amount + 0.001) {
        message.error(`Amount must be between 0.01 and Rs. ${purchase.due_amount.toFixed(2)}`);
        return;
      }

      const payload = {
        purchase_id: String(purchase.id),
        amount,
        payment_date: values.payment_date
          ? dayjs(values.payment_date).format("YYYY-MM-DD HH:mm:ss")
          : dayjs().format("YYYY-MM-DD HH:mm:ss"),
        payment_method_id: values.payment_method_id,
        bank_account_id: paymentDetails.bank_account_id || null,
        transaction_no: paymentDetails.transaction_no || null,
        reference_no: paymentDetails.reference_no || null,
        cheque_no: paymentDetails.cheque_no || null,
        bank_name: paymentDetails.bank_name || null,
        cheque_date: paymentDetails.cheque_date || null,
        note: paymentDetails.note || null,
      };

      const res = await createPaymentMutation.mutateAsync(payload);
      if (res?.success) {
        message.success("Payment recorded successfully.");
        form.resetFields();
        setPaymentDetails({
          amount: purchase.due_amount,
          note: "",
          bank_account_id: "",
          transaction_no: "",
          reference_no: "",
          cheques: [],
        });
        setPaymentMethodCode("");
        setPayModalOpen(false);
        onPaymentChange?.();
      } else {
        message.error(res?.message || "Failed to record payment.");
      }
    } catch (err: any) {
      message.error(err?.message || "An error occurred.");
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    try {
      const res = await deletePaymentMutation.mutateAsync(paymentId);
      if (res?.success) {
        message.success("Payment deleted and amounts recalculated.");
        onPaymentChange?.();
      } else {
        message.error(res?.message || "Failed to delete payment.");
      }
    } catch (err: any) {
      message.error(err?.message || "An error occurred.");
    }
  };

  const itemColumns = [
    {
      title: "Product Details",
      key: "product_details",
      render: (_: any, rec: any) => (
        <div>
          <span className="font-semibold text-gray-800 text-xs block">
            {rec.product_title}
          </span>
          <span className="text-[10px] text-gray-400 mt-0.5 block">
            SKU: {rec.product_sku || "N/A"} | Barcode: {rec.product_barcode || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Batch / Mode",
      key: "batch_details",
      render: (_: any, rec: any) => {
        const isExisting = rec.stock_option === "existing";
        const batchNo = isExisting ? rec.stock_batch_no || rec.batch_no : rec.batch_no;
        return (
          <div className="flex flex-col gap-1">
            <Tag color={isExisting ? "purple" : "blue"} className="text-[10px] m-0 w-fit">
              {isExisting ? "Existing Stock" : "New Stock"}
            </Tag>
            <span className="text-xs font-semibold text-gray-700 font-mono">
              {batchNo || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (v: string | null) => (
        <span className="text-xs text-gray-600 font-medium">
          {v ? dayjs(v).format("YYYY-MM-DD") : <Text type="secondary">No Expiry</Text>}
        </span>
      ),
    },
    {
      title: "Qty",
      key: "quantity",
      align: "center" as const,
      render: (_: any, rec: any) => (
        <Tag color="orange" className="font-semibold text-xs m-0">
          {rec.quantity} {rec.product_unit || "Pcs"}
        </Tag>
      ),
    },
    {
      title: "Cost Price",
      dataIndex: "cost_price",
      key: "cost_price",
      render: (v: number) => (
        <span className="text-xs font-medium text-gray-800">Rs. {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Retail Price",
      dataIndex: "retail_price",
      key: "retail_price",
      render: (v: number) => (
        <span className="text-xs font-medium text-green-600">Rs. {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Margin",
      dataIndex: "profit_margin",
      key: "profit_margin",
      render: (v: number | null) => (
        <span className="text-xs font-medium text-gray-600">
          {v !== null && v !== undefined ? `${v.toFixed(2)}%` : "N/A"}
        </span>
      ),
    },
    {
      title: "Wholesale",
      dataIndex: "wholesale_price",
      key: "wholesale_price",
      render: (v: number) => (
        <span className="text-xs font-medium text-blue-600">Rs. {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Disc (%)",
      dataIndex: "discount_percent",
      key: "discount_percent",
      render: (v: number) => (
        <span className="text-xs text-gray-600">{v}%</span>
      ),
    },
    {
      title: "Subtotal",
      dataIndex: "total",
      key: "total",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-900">Rs. {v.toFixed(2)}</span>
      ),
    },
  ];

  const paymentColumns = [
    {
      title: "Payment No",
      dataIndex: "payment_no",
      key: "payment_no",
      render: (v: string) => (
        <span className="font-bold text-xs text-indigo-600 font-mono">{v}</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <CalendarOutlined />
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Method",
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      render: (v: string) => (
        <Tag color="blue" className="text-[10px] font-semibold uppercase">
          {v}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="font-bold text-sm text-green-600">
          Rs. {Number(v).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Note / Details",
      dataIndex: "note",
      key: "note",
      render: (v: string, rec: any) => (
        <div className="flex flex-col gap-1.5 py-1">
          {v && <span className="text-xs text-gray-700 font-medium">{v}</span>}
          <PaymentMethodDetails
            type={rec.payment_method_code || ""}
            data={{
              transaction_no: rec.card_transaction_no || rec.bank_reference_no,
              cheque_no: rec.cheque_no,
              bank_name: rec.cheque_bank_name,
              cheque_date: rec.cheque_date,
              deposit_bank_name: rec.bank_account_name,
            }}
          />
          {!v && !rec.cheque_no && !rec.card_transaction_no && !rec.bank_reference_no && (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: any, rec: any) => (
        <Popconfirm
          title="Delete this payment?"
          description="This will reverse the payment and recalculate the outstanding balance."
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={() => handleDeletePayment(rec.id)}
        >
          <Button
            type="text"
            icon={<DeleteOutlined />}
            size="small"
            danger
            className="opacity-50 hover:opacity-100"
            loading={deletePaymentMutation.isPending}
          />
        </Popconfirm>
      ),
    },
  ];

  const payStatusColor =
    purchase.payment_status === PurchasePaymentStatus.PAID
      ? "success"
      : purchase.payment_status === PurchasePaymentStatus.PARTIAL
      ? "warning"
      : "error";

  const isFullyPaid = purchase.due_amount <= 0;

  return (
    <div className="flex flex-col gap-6 py-2 px-1">
      {purchase.status === PurchaseStatus.RETURNED && (
        <Alert
          message="Purchase Returned"
          description="This purchase was returned. Stock adjustments have been reversed."
          type="warning"
          showIcon
          className="rounded-xl"
        />
      )}

      {isCompleted && !isFullyPaid && (
        <Alert
          message={`Outstanding balance: Rs. ${purchase.due_amount.toFixed(2)}`}
          description="There is an outstanding amount due to the supplier. Record a payment below."
          type="error"
          showIcon
          icon={<ClockCircleOutlined />}
          className="rounded-xl"
          action={
            <Button
              size="small"
              type="primary"
              danger
              icon={<PlusOutlined />}
              onClick={() => setPayModalOpen(true)}
            >
              Record Payment
            </Button>
          }
        />
      )}

      {isCompleted && isFullyPaid && (
        <Alert
          message="Fully Settled"
          description="All payments have been made. This purchase is fully settled with the supplier."
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          className="rounded-xl"
        />
      )}

      {/* Header Info */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={15}>
          <Card size="small" className="rounded-xl border border-gray-200 shadow-sm h-full">
            <div>
              <div className="flex items-start justify-between border-b border-gray-100 pb-3 mb-3">
                <div>
                  <Text className="text-[10px] text-gray-400 uppercase font-bold tracking-wider block">
                    Supplier Details
                  </Text>
                  <Title level={5} style={{ margin: "2px 0 0 0" }} className="text-indigo-600">
                    <ShopOutlined className="mr-1.5" />
                    {purchase.supplier_name || "Unknown Supplier"}
                  </Title>
                </div>
                <div className="text-right">
                  <Text className="text-[10px] text-gray-400 uppercase font-bold tracking-wider block">
                    Invoice Details
                  </Text>
                  <Text className="font-bold text-gray-700 text-sm block mt-0.5">
                    <FileTextOutlined className="mr-1" /> Inv: {purchase.invoice_no || "N/A"}
                  </Text>
                  {purchase.po_no && (
                    <Text className="text-xs text-gray-400 block mt-0.5">
                      PO Reference: {purchase.po_no}
                    </Text>
                  )}
                </div>
              </div>

              <Row gutter={[12, 12]} className="text-xs">
                <Col span={12} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <CalendarOutlined className="text-gray-400" />
                    <span className="font-medium text-gray-700">Purchase Date:</span>
                    <span>{dayjs(purchase.purchase_date).format("YYYY-MM-DD")}</span>
                  </div>
                  {purchase.due_date && (
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <CalendarOutlined className="text-gray-400" />
                      <span className="font-medium text-gray-700">Payment Due:</span>
                      <span className="text-red-500 font-medium">
                        {dayjs(purchase.due_date).format("YYYY-MM-DD")}
                      </span>
                    </div>
                  )}
                </Col>
                <Col span={12} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <UserOutlined className="text-gray-400" />
                    <span className="font-medium text-gray-700">Created By:</span>
                    <span>
                      {purchase.created_by_username || `User ID: ${purchase.created_by || "System"}`}
                    </span>
                  </div>
                  {purchase.created_at && (
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <CalendarOutlined className="text-gray-400" />
                      <span className="font-medium text-gray-700">Logged At:</span>
                      <span>{dayjs(purchase.created_at).format("YYYY-MM-DD HH:mm")}</span>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={9}>
          <Card size="small" className="rounded-xl border border-gray-200 shadow-sm bg-gray-50/30">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Transaction Summary
              </span>
              <div className="flex gap-1.5">
                <Tag color="cyan" className="text-[10px] uppercase font-bold m-0">
                  {purchase.status}
                </Tag>
                <Tag color={payStatusColor} className="text-[10px] uppercase font-bold m-0">
                  {purchase.payment_status}
                </Tag>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-700">Rs. {purchase.total_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Global Discount</span>
                <span className="font-medium text-red-500">-Rs. {purchase.discount_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs pt-1.5 border-t border-dashed border-gray-200 font-semibold text-gray-800">
                <span>Grand Total</span>
                <span>Rs. {purchase.grand_total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs pt-1.5 border-t border-gray-200 font-bold text-green-600">
                <span>Total Paid</span>
                <span>Rs. {purchase.paid_amount.toFixed(2)}</span>
              </div>
              <div
                className={`flex justify-between text-sm pt-2 border-t-2 font-black rounded px-2 py-1.5 ${
                  isFullyPaid
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <span
                  className={`text-xs uppercase tracking-wide font-bold ${
                    isFullyPaid ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {isFullyPaid ? "Fully Settled" : "Remaining Due"}
                </span>
                <span className={isFullyPaid ? "text-green-600" : "text-red-600"}>
                  Rs. {purchase.due_amount.toFixed(2)}
                </span>
              </div>
            </div>
            {isCompleted && !isFullyPaid && (
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                block
                className="mt-3"
                onClick={() => setPayModalOpen(true)}
              >
                Record Payment
              </Button>
            )}
          </Card>
        </Col>
      </Row>

      {/* Payment History */}
      {isCompleted && (
        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="bg-indigo-50/60 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-2">
              <CreditCardOutlined />
              Payment History
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 font-bold">
                {payments.length} payment{payments.length !== 1 ? "s" : ""}
              </span>
              {!isFullyPaid && (
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setPayModalOpen(true)}
                >
                  Record Payment
                </Button>
              )}
            </div>
          </div>
          {paymentsLoading ? (
            <div className="p-4">
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ) : payments.length === 0 ? (
            <div className="py-8">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span className="text-gray-400 text-xs">
                    No payments recorded yet. The full amount is outstanding.
                  </span>
                }
              />
            </div>
          ) : (
            <Table
              dataSource={payments}
              columns={paymentColumns}
              pagination={false}
              size="small"
              rowKey="id"
              bordered={false}
              scroll={{ x: 700 }}
            />
          )}
        </div>
      )}

      {/* Purchased Items */}
      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gray-50/80 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">
            Purchased Itemized List
          </span>
          <span className="text-[10px] text-gray-400 font-bold">
            Total items: {purchase.items?.length || 0}
          </span>
        </div>
        <Table
          dataSource={purchase.items || []}
          columns={itemColumns}
          pagination={false}
          size="small"
          rowKey={(rec, index) => `${rec.product_id}-${index}`}
          bordered={false}
          scroll={{ x: 900 }}
        />
      </div>

      {/* Record Payment Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <CreditCardOutlined className="text-indigo-500" />
            <span>Record Supplier Payment</span>
          </div>
        }
        open={payModalOpen}
        onCancel={() => {
          setPayModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={480}
      >
        <div className="bg-gray-50 rounded-lg p-3 mb-4 flex justify-between text-sm">
          <div>
            <span className="text-gray-500 text-xs block">Supplier</span>
            <span className="font-semibold text-gray-700">
              {purchase.supplier_name}
            </span>
          </div>
          <div className="text-right">
            <span className="text-gray-500 text-xs block">Outstanding Due</span>
            <span className="font-black text-red-500 text-lg">
              Rs. {purchase.due_amount.toFixed(2)}
            </span>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleRecordPayment}
          initialValues={{
            amount: purchase.due_amount,
            payment_date: dayjs(),
          }}
        >
          <Form.Item
            label="Payment Method"
            name="payment_method_id"
            rules={[{ required: true, message: "Payment method is required." }]}
          >
            <Select
              placeholder="Select payment method"
              size="large"
              onChange={(val) => {
                const pm = paymentMethods.find((m: any) => m.id === val);
                setPaymentMethodCode(pm ? pm.code : "");
                setPaymentDetails({
                  amount: purchase.due_amount,
                  note: "",
                  bank_account_id: "",
                  transaction_no: "",
                  reference_no: "",
                  cheques: [],
                });
              }}
            >
              {paymentMethods.map((pm: any) => (
                <Option key={pm.id} value={pm.id}>
                  {pm.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {paymentMethodCode && (
            <div className="mb-4 pt-2 border-t border-gray-100">
              <PaymentOption
                paymentMethodCode={paymentMethodCode}
                value={paymentDetails}
                onChange={(val) => setPaymentDetails(val)}
                bankAccounts={bankAccounts}
              />
            </div>
          )}

          <Form.Item label="Payment Date" name="payment_date">
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              className="w-full"
              size="large"
            />
          </Form.Item>

          <div className="flex gap-2 justify-end pt-2">
            <Button
              onClick={() => {
                setPayModalOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createPaymentMutation.isPending}
              icon={<CheckCircleOutlined />}
            >
              Confirm Payment
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
